
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@/hooks/use-toast';
import AvailabilityCalendar from '@/components/availability/AvailabilityCalendar';
import WeeklySchedule from '@/components/availability/WeeklySchedule';
import DaySchedule from '@/components/availability/DaySchedule';
import ReminderSettings from '@/components/availability/ReminderSettings';
import DoctorHeader from '@/components/doctor/DoctorHeader';
import DoctorFooterNav from '@/components/doctor/DoctorFooterNav';

// Initialize default schedule
const initializeDefaultSchedule = () => {
  const defaultDay = {
    isAvailable: false,
    timeBlocks: []
  };

  return {
    monday: { ...defaultDay },
    tuesday: { ...defaultDay },
    wednesday: { ...defaultDay },
    thursday: { ...defaultDay },
    friday: { ...defaultDay },
    saturday: { ...defaultDay },
    sunday: { ...defaultDay }
  };
};

const DoctorAvailability = () => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [schedule, setSchedule] = useState(initializeDefaultSchedule());
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDay, setSelectedDay] = useState('monday');
  const navigate = useNavigate();
  const { toast } = useToast();

  // Function to handle saving changes
  const handleSaveChanges = () => {
    setIsSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSaving(false);
      toast({
        title: "Availability settings saved",
        description: "Your schedule has been updated successfully.",
      });
    }, 1000);
  };

  // Function to handle day selection from calendar
  const handleDaySelect = (day: string) => {
    setSelectedDay(day.toLowerCase());
    setActiveTab('daily');
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="min-h-screen bg-background pb-20">
      <DoctorHeader />
      
      <div className="container mx-auto p-4 pt-20 pb-24">
        <div className="flex items-center gap-2 mb-6">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="mr-1"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">My Availability</h1>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Manage Your Availability</CardTitle>
            <CardDescription>
              Set your working hours and appointment slots for patients
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="calendar" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="calendar">Calendar View</TabsTrigger>
                <TabsTrigger value="weekly">Weekly Schedule</TabsTrigger>
                <TabsTrigger value="daily">Daily Hours</TabsTrigger>
                <TabsTrigger value="reminders">Reminders</TabsTrigger>
              </TabsList>
              
              <TabsContent value="calendar">
                <AvailabilityCalendar 
                  schedule={schedule} 
                  onDaySelect={handleDaySelect}
                />
                <div className="mt-4 text-sm text-muted-foreground">
                  <p>Click on any day in the calendar to quickly edit that day's availability.</p>
                </div>
              </TabsContent>
              
              <TabsContent value="weekly">
                <WeeklySchedule schedule={schedule} setSchedule={setSchedule} />
              </TabsContent>
              
              <TabsContent value="daily">
                <div className="mb-4">
                  <select 
                    className="border rounded-md px-4 py-2 w-full sm:w-auto"
                    value={selectedDay}
                    onChange={(e) => setSelectedDay(e.target.value)}
                  >
                    {days.map((day, index) => (
                      <option key={day} value={day}>{dayNames[index]}</option>
                    ))}
                  </select>
                </div>
                <DaySchedule 
                  dayName={dayNames[days.indexOf(selectedDay)]} 
                  dayKey={selectedDay as any}
                  daySchedule={schedule[selectedDay as keyof typeof schedule]}
                  setSchedule={setSchedule}
                />
              </TabsContent>
              
              <TabsContent value="reminders">
                <ReminderSettings 
                  remindersEnabled={remindersEnabled} 
                  setRemindersEnabled={setRemindersEnabled} 
                />
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button
              variant="default"
              onClick={handleSaveChanges}
              disabled={isSaving}
            >
              {isSaving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
      
      <DoctorFooterNav />
    </div>
  );
};

export default DoctorAvailability;
